import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import {
  MatBottomSheet,
  MatBottomSheetConfig,
} from '@angular/material/bottom-sheet';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ScreenUtils } from '@sc/utils';
import { Observable, of } from 'rxjs';
import {
  AlreadyAbandonnedDialogComponent,
  AppointmentValidationDialogComponent,
  AskConfirmationDialogComponent,
  CancelAppointmentDialogComponent,
  DossierAbandonDialogComponent,
  DossierAbandonFitterDialogComponent,
  FitterCommentsBottomSheetComponent,
  FitterCommentsDialogComponent,
  GpdrInfoRedirectDialogComponent,
  ItineraryBottomSheetComponent,
  ItineraryDialogComponent,
  NoShowCommentDialogComponent,
  RestartDeclarationDialogComponent,
  RestrictionWarnBottomSheetComponent,
  RestrictionWarnDialogComponent,
  RouterInfoActionDialogComponent,
  DossierCommentsDialogComponent,
  PhoneNumberDialogComponent,
} from '../../components';
import { CompleteAppointmentFitterDialogComponent } from '../../components/dialogs/complete-appointment-fitter-dialog';
import {
  OverlayBottomSheetComponent,
  OverlayDialogComponent,
  OverlayOutputs,
  OverlayParams,
  OverlayType,
} from '../../types';
import { PhoneNumberBottomSheetComponent } from '../../components/bottom-sheets/phone-number-bottom-sheet/phone-number-bottom-sheet.component';

@Injectable({
  providedIn: 'root',
})
export class AdaptableOverlayService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly bottomSheet: MatBottomSheet,
    private overlay: Overlay
  ) {}

  showOverlay<T extends OverlayType>(
    params: OverlayParams<T>,
    forceBottomSheet = false
  ): Observable<OverlayOutputs<T>> {
    switch (params.type) {
      case 'appointment-validation':
        return this.choseOverlay(
          params,
          AppointmentValidationDialogComponent,
          null,
          { panelClass: 'appointment-validation-dialog' },
          { panelClass: 'appointment-validation-bottom-sheet' }
        );
      case 'itinerary':
        return this.choseOverlay(
          params,
          ItineraryDialogComponent,
          ItineraryBottomSheetComponent,
          { panelClass: 'itinerary-dialog' },
          { panelClass: 'itinerary-bottom-sheet' }
        );
      case 'fitter-comments':
        return this.choseOverlay(
          params,
          FitterCommentsDialogComponent,
          FitterCommentsBottomSheetComponent,
          { panelClass: 'fitter-comments-dialog' },
          { panelClass: 'fitter-comments-bottom-sheet' }
        );
      case 'dossier-abandon':
        return this.choseOverlay(
          params,
          DossierAbandonDialogComponent,
          null,
          { panelClass: 'dossier-abandon-dialog' },
          { panelClass: 'dossier-abandon-bottom-sheet' }
        );
      case 'dossier-abandon-fitter':
        return this.choseOverlay(
          params,
          DossierAbandonFitterDialogComponent,
          null,
          { panelClass: 'dossier-abandon-fitter-dialog' }
        );
      case 'complete-appointment':
        return this.choseOverlay(
          params,
          CompleteAppointmentFitterDialogComponent,
          null,
          { panelClass: 'complete-appointment-fitter-dialog' }
        );
      case 'cancel-appointment':
        return this.choseOverlay(
          params,
          CancelAppointmentDialogComponent,
          null,
          { panelClass: 'cancel-appointment-dialog' },
          { panelClass: 'cancel-appointment-bottom-sheet' }
        );
      case 'router-infos-action':
        return this.choseOverlay(
          params,
          RouterInfoActionDialogComponent,
          null,
          {
            panelClass: 'operation-type-info-dialog',
          }
        );
      case 'no-show-comment':
        return this.choseOverlay(params, NoShowCommentDialogComponent, null, {
          panelClass: 'no-show-comment-dialog',
        });
      case 'gpdr-info-redirect':
        return this.choseOverlay(
          params,
          GpdrInfoRedirectDialogComponent,
          null,
          {}
        );
      case 'restriction-warn':
        return this.choseOverlay(
          params,
          RestrictionWarnDialogComponent,
          RestrictionWarnBottomSheetComponent,
          { panelClass: 'restriction-warn-dialog' },
          { panelClass: 'restriction-warn-bottom-sheet' }
        );
      case 'restart-declaration':
        return this.choseOverlay(
          params,
          RestartDeclarationDialogComponent,
          null,
          { panelClass: 'restart-declaration-dialog' },
          null
        );
      case 'already-abandonned':
        return this.choseOverlay(
          params,
          AlreadyAbandonnedDialogComponent,
          null,
          { panelClass: 'already-abandonned-dialog' },
          null
        );
      case 'ask-confirmation':
        return this.choseOverlay(
          params,
          AskConfirmationDialogComponent,
          null,
          { panelClass: 'ask-confirmation-dialog' },
          null
        );
      case 'dossier-comments':
        return this.choseOverlay(
          params,
          DossierCommentsDialogComponent,
          null,
          { panelClass: 'dossier-comments-dialog' },
          null
        );
      case 'phone-number':
        return this.choseOverlay(
          params,
          PhoneNumberDialogComponent,
          PhoneNumberBottomSheetComponent,
          {
            panelClass: 'phone-number-dialog',
            hasBackdrop: false,
            disableClose: true,
            position: {
              bottom: '16px',
              right: '16px',
            },
            role: 'alertdialog',
            scrollStrategy: this.overlay.scrollStrategies.noop(),
          },
          {
            panelClass: 'phone-number-sheet',
            hasBackdrop: false,
            disableClose: true,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            direction: 'ltr',
          },
          forceBottomSheet
        );

      default:
        break;
    }
  }

  private choseOverlay<T extends OverlayType>(
    params: OverlayParams<T>,
    desktopComponent: ComponentType<OverlayDialogComponent<T>>,
    mobileComponent: ComponentType<OverlayBottomSheetComponent<T>>,
    dialogConfig: Partial<MatDialogConfig<OverlayParams<T>['input']>> = {},
    bottomSheetConfig: Partial<
      MatBottomSheetConfig<OverlayParams<T>['input']>
    > = {},
    forceBottomSheet = false
  ): Observable<OverlayOutputs<T>> {
    if (
      (ScreenUtils.isDesktopResolution() || !mobileComponent) &&
      !!desktopComponent &&
      !forceBottomSheet
    ) {
      const panelClasses: string[] = [
        'sc-overlay-dialog',
        'sc-scrollable-dialog',
      ];
      if (Array.isArray(dialogConfig.panelClass)) {
        panelClasses.push(...dialogConfig.panelClass);
      } else if (dialogConfig.panelClass) {
        panelClasses.push(dialogConfig.panelClass);
      }

      // Use dialog service if the screen size is desktop
      return this.dialog
        .open<
          OverlayDialogComponent<T>,
          OverlayParams<T>['input'],
          OverlayOutputs<T>
        >(desktopComponent, {
          data: params.input,
          autoFocus: false,
          closeOnNavigation: true,
          disableClose: true,
          ...dialogConfig,
          panelClass: panelClasses,
        })
        .afterClosed();
    } else if (
      (ScreenUtils.isMobileResolution() ||
        !desktopComponent ||
        forceBottomSheet) &&
      !!mobileComponent
    ) {
      const panelClasses: string[] = ['sc-overlay-bottom-sheet'];
      if (Array.isArray(bottomSheetConfig.panelClass)) {
        panelClasses.push(...bottomSheetConfig.panelClass);
      } else if (bottomSheetConfig.panelClass) {
        panelClasses.push(bottomSheetConfig.panelClass);
      }

      // Use bottom sheet service if the screen size is mobile
      return this.bottomSheet
        .open<
          OverlayBottomSheetComponent<T>,
          OverlayParams<T>['input'],
          OverlayOutputs<T>
        >(mobileComponent, {
          data: params.input,
          autoFocus: false,
          closeOnNavigation: true,
          ...bottomSheetConfig,
          panelClass: panelClasses,
        })
        .afterDismissed();
    } else {
      return of(null);
    }
  }
}
